import React from 'react';
import {
    CheckCircle2,
    Rocket,
    Target,
    TrendingUp,
    Layers,
    BarChart3,
    ShieldCheck,
    Workflow,
    Zap,
    Mic,
    Film,
    FileText,
    Search,
    Ban,
    Sparkles,
    MessageCircle,
    Flame,
    Repeat
} from 'lucide-react';

/* ── primitives ─────────────────────────────────────────────────────────── */

const SectionTag = ({ icon: IconComp, label, color = 'indigo' }) => {
    const colorClasses = {
        indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-300',
        rose: 'bg-rose-500/10 border-rose-500/20 text-rose-300',
        emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300',
        amber: 'bg-amber-500/10 border-amber-500/20 text-amber-300',
        purple: 'bg-purple-500/10 border-purple-500/20 text-purple-300'
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-xs sm:text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const Check = ({ children }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-400" />
        <span className="leading-relaxed text-slate-200 text-sm">{children}</span>
    </li>
);

const StatCard = ({ value, label, sub, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-900/40 to-slate-900/80 border-indigo-500/30',
        emerald: 'from-emerald-900/40 to-slate-900/80 border-emerald-500/30',
        purple: 'from-purple-900/40 to-slate-900/80 border-purple-500/30',
        amber: 'from-amber-900/40 to-slate-900/80 border-amber-500/30',
        rose: 'from-rose-900/40 to-slate-900/80 border-rose-500/30'
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-5 sm:p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-1 whitespace-nowrap leading-none">{value}</div>
            <div className="text-xs sm:text-sm font-semibold text-white/80 mt-2">{label}</div>
            {sub && <div className="text-[10px] sm:text-xs text-white/50 mt-1.5">{sub}</div>}
        </div>
    );
};

const Panel = ({ children, className = '' }) => (
    <div className={`bg-slate-900/70 border border-slate-700/60 rounded-2xl p-5 sm:p-7 ${className}`}>{children}</div>
);

/* ── data ───────────────────────────────────────────────────────────────── */

const LOOP = [
    { icon: Search, who: 'SocialSEO', title: 'We research', body: 'Every week we scrape your 8 reference creators, score what broke out vs their own normal, and translate the winners into your true versions.' },
    { icon: FileText, who: 'SocialSEO', title: 'Monday report lands', body: '8–10 idea cards. Each card: the proven source with live numbers, a draft hook, and 3–5 specific questions. One card is always the AI experiment of the week.' },
    { icon: Target, who: 'Radha', title: 'You pick ~5, kill the rest', body: 'THIS is your veto moment. "If I don\'t believe it, we don\'t film it" happens here, at the idea stage — not after scripting.' },
    { icon: Mic, who: 'Radha', title: 'Voice-note the answers', body: 'For your picks, answer the questions as voice notes in the group. Whenever suits you. Talking, not writing. No calls.' },
    { icon: FileText, who: 'SocialSEO', title: 'We script from your words', body: 'High-retention structure, your language. On scripts you request changes (2 rounds) — the belief filter already happened at the idea stage.' },
    { icon: Film, who: 'Both', title: 'You film, we ship', body: 'Phone or Osmo, drop in Drive. We edit, caption, post everywhere, and your numbers come back in next Monday\'s report.' }
];

const OUTLIERS = [
    { ratio: '49.5x', views: '360K', handle: '@thelondonfounder', post: '"How to make an AI app look good" — show-the-work AI', accent: 'text-emerald-400' },
    { ratio: '24.2x', views: '2.9M', handle: '@storieswithkiki', post: '"i want to do everything and so i do nothing" — still her #1', accent: 'text-indigo-400' },
    { ratio: '23.2x', views: '356K', handle: '@breebecoming', post: '"The audacity of 20-something me to commit to that" — confession, 10 days old', accent: 'text-rose-400' },
    { ratio: '41.7x', views: '303K', handle: '@thelondonfounder', post: '"We built our app in 48 hours with $0"', accent: 'text-emerald-400' },
    { ratio: '13.8x', views: '1.7M', handle: '@storieswithkiki', post: '"the more i heal the less ambitious i become"', accent: 'text-indigo-400' },
    { ratio: '10.9x', views: '167K', handle: '@breebecoming', post: '"I\'m grateful to stay home with my son… and I don\'t always love it"', accent: 'text-rose-400' }
];

const CARDS = [
    { n: 1, tag: 'LAUNCH', title: 'The intro post', hook: '"I\'m 30. I came back home to build a factory. Let me explain."', proven: '@personalbrandlaunch sequence — 249K views' },
    { n: 2, tag: 'LAUNCH · KEY', title: 'The Underdog Post', hook: '"Running a business here costs nearly double. Nobody talks about why. I will."', proven: 'The most important video of the plan — the villain is the system and its friction, never the place, never the people' },
    { n: 3, tag: 'PHILOSOPHY', title: 'Waiting to feel passionate', hook: '"You\'re waiting to feel passionate before you start. That\'s the whole reason you haven\'t."', proven: 'Kiki — 2.9M views, 290K likes' },
    { n: 4, tag: 'PHILOSOPHY', title: 'I failed the same class 3 times', hook: '"I failed the same class three times. It\'s the most useful thing that ever happened to me."', proven: 'Masha — 2.48M, re-ran for another 1.16M' },
    { n: 5, tag: 'CONFESSION', title: 'Good at things = avoiding this', hook: '"I\'m good at things. That\'s exactly why I\'ve been avoiding this."', proven: 'Bree — 353K, her biggest ever' },
    { n: 6, tag: 'AI EXPERIMENT', title: 'Invoice photo → spreadsheet', hook: '"My factory runs on paper. I\'m dragging it into 2026 with one prompt. Day 1."', proven: 'londonfounder format — 49.5x her normal, hottest format in the whole set' },
    { n: 7, tag: 'BUILDING', title: 'The week nothing happened', hook: '"Nothing happened at the factory this week. Let me tell you why I\'m posting anyway."', proven: 'Bree\'s mechanic — kills the "what did you do this week" problem forever' },
    { n: 8, tag: 'PHILOSOPHY', title: 'The real reason I\'m building', hook: '"Nobody wants to say the real reason they\'re building something. So I will."', proven: 'Kiki 41K + Twinkle confession 170K' }
];

/* ── page ───────────────────────────────────────────────────────────────── */

export default function RadhaStrategyV3() {
    return (
        <div className="min-h-screen bg-slate-950 text-white font-sans">
            {/* hero */}
            <header className="relative overflow-hidden border-b border-slate-800">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-950 via-slate-950 to-slate-950" />
                <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24 text-center">
                    <SectionTag icon={Sparkles} label="Personal Brand · Strategy v3" color="indigo" />
                    <h1 className="text-3xl sm:text-5xl md:text-6xl font-black leading-tight mb-6">
                        Your strategy <span className="text-indigo-400">is</span> the strategy.
                    </h1>
                    <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                        We went through your brief and your voice note properly. You're right, and the data backs you —
                        including fresh numbers we pulled this week. This page is your plan, accepted in full,
                        plus the delivery system we're wrapping around it.
                    </p>
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
                        <StatCard value="3–10x" label="Belief beats business" sub="across every reference" accent="indigo" />
                        <StatCard value="274" label="Videos analysed" sub="8 accounts, live scrape" accent="emerald" />
                        <StatCard value="5/wk" label="3 philosophy + 2 building" sub="your split, kept" accent="purple" />
                        <StatCard value="0" label="Standing calls" sub="voice notes, your time" accent="amber" />
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20 space-y-16 sm:space-y-24">

                {/* verdict */}
                <section>
                    <SectionTag icon={TrendingUp} label="What the data says" color="emerald" />
                    <h2 className="text-2xl sm:text-4xl font-black mb-6">We re-checked your thesis with a fresh scrape. It holds.</h2>
                    <p className="text-slate-300 leading-relaxed mb-8 max-w-3xl">
                        This week we scraped all 8 of your reference creators — 274 videos — and scored every video against
                        that account's own median, so small and big accounts compare fairly. The two breakout engines across
                        the whole set are exactly what your brief said: <span className="text-white font-semibold">identity / confession
                        posts</span> and <span className="text-white font-semibold">show-the-work AI posts</span>.
                    </p>
                    <div className="overflow-x-auto rounded-2xl border border-slate-700/60">
                        <table className="w-full text-left text-sm min-w-[560px]">
                            <thead>
                                <tr className="bg-slate-900 text-slate-400 uppercase text-[11px] tracking-wider">
                                    <th className="px-4 py-3">Breakout</th>
                                    <th className="px-4 py-3">Views</th>
                                    <th className="px-4 py-3">Account</th>
                                    <th className="px-4 py-3">The post</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800">
                                {OUTLIERS.map((o, i) => (
                                    <tr key={i} className="bg-slate-950/60">
                                        <td className={`px-4 py-3 font-black whitespace-nowrap ${o.accent}`}>{o.ratio}</td>
                                        <td className="px-4 py-3 font-bold text-white whitespace-nowrap">{o.views}</td>
                                        <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{o.handle}</td>
                                        <td className="px-4 py-3 text-slate-300">{o.post}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-slate-500 text-xs mt-3">Breakout = views ÷ that account's own median. Scraped 15 Jul 2026. One extra find: proven personal stories re-run 6+ months later break out twice — winning topics get second versions.</p>
                </section>

                {/* pillars */}
                <section>
                    <SectionTag icon={Layers} label="The pillars — yours, kept" color="purple" />
                    <h2 className="text-2xl sm:text-4xl font-black mb-8">Two pillars. Confession is the engine.</h2>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <Panel>
                            <div className="flex items-center gap-2 mb-3">
                                <Flame className="w-5 h-5 text-indigo-400" />
                                <h3 className="font-black text-lg">Pillar 1 — Identity / Philosophy <span className="text-indigo-400">· 3/wk</span></h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                The growth engine, not the filler. Radical responsibility, agency, ambition and the guilt that
                                comes with it, purpose is built not found. Register: <span className="text-white font-semibold">confession,
                                pointed inward</span> — you admit, you never attack.
                            </p>
                            <p className="text-slate-400 text-xs">Models: @storieswithkiki · @mashaliu · @nanadelrey · @breebecoming. Twinkle &amp; Jessi Jean = delivery psychology only, never topics.</p>
                        </Panel>
                        <Panel>
                            <div className="flex items-center gap-2 mb-3">
                                <Rocket className="w-5 h-5 text-emerald-400" />
                                <h3 className="font-black text-lg">Pillar 2 — Building <span className="text-emerald-400">· 2/wk</span></h3>
                            </div>
                            <p className="text-slate-300 text-sm leading-relaxed mb-4">
                                The true story: a brand-new factory, zero years old, built while the industrial era gets erased by
                                the technological one — and while a friction tax at every level nearly doubles the cost of everything.
                                The villain is the system, never the place or the people. You're merging the two eras anyway.
                                <span className="text-white font-semibold"> "Nothing happened this week" is also a video.</span>
                            </p>
                            <p className="text-slate-400 text-xs">Models: @thelondonfounder · @nanadelrey · @avnibarman_ · @personalbrandlaunch. Inside this pillar: the AI thread — you building with Claude, visibly learning. Nobody in your space documents this.</p>
                        </Panel>
                    </div>
                    <Panel className="mt-5 border-amber-500/30">
                        <div className="flex items-center gap-2 mb-2">
                            <Zap className="w-5 h-5 text-amber-400" />
                            <h3 className="font-black">The launch sequence</h3>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed">
                            Post 1: who I am, where I'm going. Post 2: <span className="text-white font-semibold">THE UNDERDOG POST</span> —
                            new factory, an industry being erased by the technological era, and a system whose friction nearly
                            doubles the cost of building. Doing it anyway. That's not a weakness we manage; it's the reason
                            people will root for you. Posts 3+: each video is one step of the build.
                            Then the run: <span className="text-white font-semibold">one month, no pivots</span>, then we read the data together.
                        </p>
                    </Panel>
                </section>

                {/* weekly system */}
                <section>
                    <SectionTag icon={Workflow} label="The weekly system" color="indigo" />
                    <h2 className="text-2xl sm:text-4xl font-black mb-3">No calls. One Monday report. Your veto lives at the idea stage.</h2>
                    <p className="text-slate-400 text-sm mb-8 max-w-3xl">Your total time cost each week: ~10 minutes picking cards, ~20–30 minutes of voice notes, plus filming.</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {LOOP.map((s, i) => (
                            <Panel key={i} className="relative">
                                <div className="absolute -top-3 -left-2 w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center font-black text-sm">{i + 1}</div>
                                <div className="flex items-center gap-2 mb-2 mt-1">
                                    <s.icon className="w-4 h-4 text-indigo-300" />
                                    <span className={`text-[10px] font-bold uppercase tracking-wider ${s.who === 'Radha' ? 'text-rose-300' : s.who === 'Both' ? 'text-amber-300' : 'text-emerald-300'}`}>{s.who}</span>
                                </div>
                                <h3 className="font-bold mb-2">{s.title}</h3>
                                <p className="text-slate-300 text-xs leading-relaxed">{s.body}</p>
                            </Panel>
                        ))}
                    </div>
                    <Panel className="mt-5 border-slate-600">
                        <div className="flex items-center gap-2 mb-2">
                            <Repeat className="w-5 h-5 text-slate-300" />
                            <h3 className="font-black">Safety valve</h3>
                        </div>
                        <p className="text-slate-300 text-sm">If two weeks of voice-note answers come back thin, we add one monthly extraction call — because ideas come out of you when you talk. We don't let the system quietly starve.</p>
                    </Panel>
                </section>

                {/* report 1 preview */}
                <section>
                    <SectionTag icon={FileText} label="Weekly Report #1 — ready" color="rose" />
                    <h2 className="text-2xl sm:text-4xl font-black mb-3">Your first 8 idea cards.</h2>
                    <p className="text-slate-400 text-sm mb-8 max-w-3xl">Pick ~5, kill the rest. Full card detail (with the voice-note questions) comes in the report itself the moment you approve the plan.</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {CARDS.map((c) => (
                            <Panel key={c.n} className={c.n === 2 ? 'border-amber-500/40' : ''}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${c.n === 2 ? 'text-amber-300 border-amber-500/30 bg-amber-500/10' : 'text-indigo-300 border-indigo-500/30 bg-indigo-500/10'}`}>{c.tag}</span>
                                    <span className="text-slate-600 font-black text-lg">#{c.n}</span>
                                </div>
                                <h3 className="font-bold mb-2">{c.title}</h3>
                                <p className="text-slate-200 text-sm italic leading-relaxed mb-3">{c.hook}</p>
                                <p className="text-slate-500 text-[11px]">{c.proven}</p>
                            </Panel>
                        ))}
                    </div>
                </section>

                {/* boundaries */}
                <section>
                    <SectionTag icon={ShieldCheck} label="Your boundaries, locked" color="amber" />
                    <h2 className="text-2xl sm:text-4xl font-black mb-8">Hard lines, applied to every script.</h2>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Panel>
                            <Ban className="w-5 h-5 text-rose-400 mb-2" />
                            <p className="text-slate-200 text-sm leading-relaxed">The family situation stays private. The philosophy it taught you can be content — the story itself never is.</p>
                        </Panel>
                        <Panel>
                            <Ban className="w-5 h-5 text-rose-400 mb-2" />
                            <p className="text-slate-200 text-sm leading-relaxed">Nothing product, packaging, or website related until your trademark clears. Those videos stay queued, not killed.</p>
                        </Panel>
                        <Panel>
                            <Ban className="w-5 h-5 text-rose-400 mb-2" />
                            <p className="text-slate-200 text-sm leading-relaxed">No money figures, ever. "I want money" as a confession is your line from the brief — confirm you're comfortable saying it on camera.</p>
                        </Panel>
                    </div>
                </section>

                {/* approval */}
                <section>
                    <SectionTag icon={MessageCircle} label="Approved · 16 Jul 2026" color="emerald" />
                    <h2 className="text-2xl sm:text-4xl font-black mb-6">Locked in. Report #1 lands Monday.</h2>
                    <Panel>
                        <ul className="space-y-4">
                            <Check>Plan approved — your strategy, our delivery system on top. One month, no pivots.</Check>
                            <Check>Money line: <span className="font-semibold text-white">yes to the sentiment</span> (money and freedom, no pretending it's a passion project). Exact wording locks at script stage — it lands as "done apologising for wanting more," never "I just want to be rich."</Check>
                            <Check>You're never pinned to one place — it's <span className="font-semibold text-white">"back home,"</span> half Dubai, half Zambia.</Check>
                            <Check>Staffing rule: <span className="font-semibold text-white">you originate it, the person stays anonymous, and the lesson is about you.</span> Commentary on your actual team or anyone nameable is off permanently.</Check>
                        </ul>
                    </Panel>
                    <p className="text-center text-slate-500 text-xs mt-10">SocialSEO LLP · Personal Brand Program · updated 16 Jul 2026</p>
                </section>
            </main>
        </div>
    );
}
